<template>
  <div class="tf">
    <!-- название команды живёт в шапке карточки: в хлебных крошках оно дублировалось дважды -->
    <Breadcrumbs main="Команды" title="Карточка команды"/>

    <!-- ======================= модалки ======================= -->
    <b-modal centered title="Переименовать команду" v-model="isShowRenameModal" hide-footer>
      <label class="tf-label">Новое название</label>
      <b-form-input v-model="newTeamName" :state="newTeamName ? isRenameValid : null"
                    placeholder="Название команды" @keyup.enter="confirmRename"/>
      <div class="tf-hint">Минимум 2 символа.</div>
      <div v-if="renameError" class="tf-err">{{ renameError }}</div>
      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="tf-btn-ghost mr-2" :disabled="isRenaming"
                @click="isShowRenameModal = false">Отмена
        </button>
        <button type="button" class="tf-btn-primary" :disabled="isRenaming || !isRenameValid"
                @click="confirmRename">
          <i class="fa" :class="isRenaming ? 'fa-spinner tf-spin' : 'fa-check'"></i>
          {{ isRenaming ? 'Сохраняем…' : 'Сохранить' }}
        </button>
      </div>
    </b-modal>

    <b-modal centered title="Удаление команды" v-model="isShowDeleteTeamModal" hide-footer>
      <p>Вы действительно хотите удалить команду <strong>{{ team.name }}</strong>?</p>
      <div v-if="deleteTeamError" class="tf-err">{{ deleteTeamError }}</div>
      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="tf-btn-ghost mr-2" :disabled="isDeletingTeam"
                @click="isShowDeleteTeamModal = false">Отмена
        </button>
        <button type="button" class="tf-btn-primary ui-btn-danger-solid" :disabled="isDeletingTeam"
                @click="confirmDeleteTeam">
          <i class="fa" :class="isDeletingTeam ? 'fa-spinner tf-spin' : 'fa-trash-o'"></i>
          {{ isDeletingTeam ? 'Удаляем…' : 'Удалить' }}
        </button>
      </div>
    </b-modal>

    <b-modal centered :title="`Рейтинг команды ${team.name || ''}`" v-model="show" hide-footer>
      <label class="tf-label">Рейтинг</label>
      <b-form-input type="number" v-model="newRating"/>
      <label class="tf-label mt-3">Описание</label>
      <b-form-textarea v-model="newDescription" rows="3"/>
      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="tf-btn-ghost mr-2" @click="show = false">Отмена</button>
        <button type="button" class="tf-btn-primary" @click="acceptTeamRating">
          <i class="fa fa-check"></i> Добавить
        </button>
      </div>
    </b-modal>

    <b-modal id="modal-edit-avatar" title="Логотип команды" hide-footer centered>
      <div class="ui-logo-modal">
        <img :src="logoUrl" class="ui-logo-modal__img"
             @error="event => event.target.src = TeamImagePug"/>
      </div>
      <button type="button" class="tf-btn-primary w-100 mt-3" @click="handleLogoClick">
        <i class="fa fa-upload"></i> Загрузить новый
      </button>
      <input ref="logoInput" type="file" accept="image/*" style="display: none;" @change="handleLogoSelect">
      <button v-if="hasLogo" type="button" class="tf-btn-ghost w-100 mt-2"
              @click="handleDownloadTeamAvatar(team.logo.original_url, `${team.name}-avatar`)">
        <i class="fa fa-download"></i> Скачать
      </button>
      <button v-if="hasLogo" type="button" class="tf-btn-ghost ui-btn-danger w-100 mt-2" @click="deleteLogo">
        <i class="fa fa-trash-o"></i> Удалить логотип
      </button>
    </b-modal>

    <div class="container-fluid tf-wrap">

      <!-- ======================= шапка команды ======================= -->
      <div class="ui-hero">
        <button type="button" class="tf-back" @click="goBack" v-b-tooltip.hover.bottom="'К списку команд'">
          <i class="fa fa-arrow-left"></i>
        </button>

        <button type="button" class="ui-hero__logo" @click="handleOpenEditAvatar"
                v-b-tooltip.hover.bottom="'Логотип команды'">
          <img :src="logoUrl" alt="" @error="event => event.target.src = TeamImagePug"/>
          <span class="ui-hero__logo-edit"><i class="fa fa-camera"></i></span>
        </button>

        <div class="ui-hero__title">
          <div class="ui-hero__name-row">
            <h1 class="ui-hero__name" :class="{ 'is-placeholder': !team.name }">{{ team.name || 'Команда' }}</h1>
            <span v-if="isProDivision" class="tf-badge tf-badge--warn"><i class="fa fa-star"></i> PRO</span>
            <span v-if="bans.length" class="tf-badge tf-badge--danger"><i class="fa fa-ban"></i> В бане</span>
            <span v-if="team.deleted_at" class="tf-badge tf-badge--danger">Удалена</span>
          </div>

          <div class="ui-hero__meta">
            <button type="button" class="ui-copy" @click="copyValue(team.id, 'ID')"
                    v-b-tooltip.hover.bottom="'Скопировать ID'">
              ID {{ team.id || '—' }} <i class="fa fa-copy"></i>
            </button>
            <i class="tf-dot"></i>
            <span>{{ gameName }}</span>
            <i class="tf-dot"></i>
            <span>{{ countryName }}</span>
            <i class="tf-dot"></i>
            <span>создана {{ formatDateTime(team.created_at) }}</span>
            <i class="tf-dot"></i>
            <span>создатель {{ owner || '—' }}</span>
          </div>
        </div>

        <dl class="ui-stats">
          <div>
            <dt>Участников</dt>
            <dd>{{ membersCount }}</dd>
          </div>
          <div>
            <dt>Турниров</dt>
            <dd>{{ team.tournaments_count || 0 }}</dd>
          </div>
          <div>
            <dt>Рейтинг</dt>
            <dd>{{ ratingValue }}</dd>
          </div>
          <div>
            <dt>Дивизион</dt>
            <dd>{{ divisionName }}</dd>
          </div>
        </dl>

        <div class="ui-hero__actions">
          <router-link :to="{ name: 'teams_add_player', params: { id: $route.params.id } }" class="tf-btn-primary">
            <i class="fa fa-user-plus"></i> Добавить игрока
          </router-link>
          <b-dropdown right no-caret variant="link" toggle-class="ui-kebab" menu-class="ui-menu"
                      title="Ещё действия">
            <template #button-content><i class="fa fa-ellipsis-h"></i></template>
            <b-dropdown-item @click="openRenameModal">
              <i class="fa fa-pencil"></i> Переименовать
            </b-dropdown-item>
            <b-dropdown-item @click="handleOpenEditAvatar">
              <i class="fa fa-picture-o"></i> Логотип команды
            </b-dropdown-item>
            <b-dropdown-divider/>
            <b-dropdown-item link-class="ui-menu__danger" @click="openDeleteTeamModal">
              <i class="fa fa-trash-o"></i> Удалить команду
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <!-- ======================= табы ======================= -->
      <div class="ui-tabs" ref="tabsTop">
        <button v-for="tab in tabs" :key="tab.key" type="button"
                class="ui-tab" :class="{ 'is-on': activeTab === tab.key }"
                @click="goTab(tab.key)">
          <i class="fa" :class="tab.icon"></i>
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== null" class="ui-count">{{ tab.count }}</span>
        </button>
      </div>

      <div class="ui-main">

          <!-- ============ ОБЩЕЕ ============ -->
          <section v-show="activeTab === 'main'" class="tf-sec">

            <div v-if="bans.length" class="tf-alert mb-3">
              <i class="fa fa-ban tf-alert__icon"></i>
              <div class="tf-alert__body">
                <div class="tf-alert__title">Активных банов: {{ bans.length }}</div>
                <div v-for="ban in bans" :key="'ban' + ban.id" class="ui-ban">
                  <b>{{ ban.game_name || 'Команда' }}</b>
                  <span>с {{ ban.from || '—' }} до {{ ban.to || '—' }}</span>
                  <em v-if="ban.description">{{ ban.description }}</em>
                </div>
              </div>
            </div>

            <div v-if="team.deleted_at" class="tf-alert mb-3">
              <i class="fa fa-archive tf-alert__icon"></i>
              <div class="tf-alert__body">
                <div class="tf-alert__title">Команда удалена {{ formatDateTime(team.deleted_at) }}</div>
              </div>
            </div>

            <!-- Pro Division -->
            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-star"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">Pro Division</h3>
                  <p class="tf-card__sub">Участие в профессиональных турнирах</p>
                </div>
                <b-spinner small v-if="isLoadingToggleProDivision"></b-spinner>
                <b-form-checkbox class="ui-switch" switch v-model="isProDivision"
                                 :disabled="isLoadingToggleProDivision" @change="toggleProDivision"/>
              </div>
              <div v-if="isProDivision" class="tf-card__body">
                <div class="tf-label">Регион</div>
                <div class="ui-region">
                  <b-form-select :disabled="!!selectedProRegionId" v-model="selectedProRegionId"
                                 :options="regionsOptions"/>
                  <button type="button" class="tf-btn-ghost" :disabled="!selectedProRegionId"
                          @click="detachProTeamRegion(team.id)">
                    Отвязать
                  </button>
                </div>
                <div class="tf-hint">Регион привязывается один раз — чтобы сменить, сначала отвяжите текущий.</div>
              </div>
            </div>

            <!-- Участники -->
            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-users"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">Участники</h3>
                </div>
              </div>
              <div class="tf-card__body ui-flush">
                <TeamMembersTable v-if="hasMembersTable"
                                  :game-slug="team.game.slug"
                                  :team-members="members"/>
                <div v-else class="ui-muted p-3">Состав недоступен для этой дисциплины</div>
              </div>
            </div>

            <!-- Баннер -->
            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-picture-o"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">Баннер команды</h3>
                  <p class="tf-card__sub">Показывается на публичной странице команды</p>
                </div>
              </div>
              <div class="tf-card__body">
                <div v-if="!bannerPreview && !team.banner"
                     class="ui-drop" :class="{ 'is-over': isDragover }"
                     @dragenter.prevent="isDragover = true"
                     @dragleave.prevent="isDragover = false"
                     @dragover.prevent
                     @drop.prevent="handleFileDrop"
                     @click="$refs.fileInput.click()">
                  <input ref="fileInput" type="file" accept="image/*" style="display: none;"
                         @change="handleFileSelect">
                  <i class="fa fa-cloud-upload fa-2x"></i>
                  <span>{{ isDragover ? 'Отпустите изображение' : 'Перетащите изображение или нажмите, чтобы выбрать' }}</span>
                </div>

                <div v-else-if="bannerPreview" class="ui-banner">
                  <img :src="bannerPreview" class="ui-banner__img"/>
                  <div class="ui-banner__actions">
                    <button type="button" class="tf-btn-primary" @click="uploadBanner">
                      <i class="fa fa-check"></i> Загрузить
                    </button>
                    <button type="button" class="tf-btn-ghost" @click="cancelUpload">Отмена</button>
                  </div>
                </div>

                <div v-else class="ui-banner">
                  <img :src="team.banner.original_url" class="ui-banner__img"/>
                  <div class="ui-banner__actions">
                    <button type="button" class="tf-btn-ghost ui-btn-danger" @click="deleteBanner">
                      <i class="fa fa-trash-o"></i> Удалить баннер
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- ============ ТУРНИРЫ ============ -->
          <section v-show="activeTab === 'tournaments'" class="tf-sec">
            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-trophy"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">Турниры команды</h3>
                </div>
              </div>
              <div class="tf-card__body ui-flush">
                <b-table
                    class="ui-table-bv"
                    responsive borderless hover show-empty
                    :fields="tournamentLogsFields"
                    :items="teamTournamentLogs"
                    empty-text="Турниров нет">
                  <template #cell(name)="data">
                    {{ data.item.tournament.name }}
                  </template>
                  <template #cell(format)="data">
                    {{ data.item.tournament.format.name }}
                  </template>
                  <template #cell(details)="data">
                    <button type="button" class="ui-act" @click="showTournDetails(data)"
                            v-b-tooltip.hover.top="'Составы по стадиям'">
                      <i class="fa" :class="data.detailsShowing ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    </button>
                  </template>
                  <template #row-details="tournamentData">
                    <div v-if="tournamentData.item && tournamentData.item.stage_team
                               && tournamentData.item.stage_team.tournament_team
                               && tournamentData.item.stage_team.tournament_team.stage_teams"
                         class="ui-subtable">
                      <b-table :items="tournamentData.item.stage_team.tournament_team.stage_teams"
                               :fields="tournamentLogFields" borderless responsive show-empty
                               class="ui-table-bv">
                        <template #cell(stage)="data">
                          {{ data.item && data.item.stage ? data.item.stage.name : '—' }}
                        </template>
                        <template #cell(members)="data">
                          <div v-for="member in data.item.stage_team_members" :key="'memberId' + member.id">
                            <div v-for="profile in (member.user && member.user.game_profiles) || []" :key="profile.id">
                              <span v-if="profile.fields && profile.fields.nick">{{ profile.fields.nick }}</span>
                            </div>
                          </div>
                        </template>
                        <template #cell(game_id)="data">
                          <div v-for="member in data.item.stage_team_members" :key="'gameId' + member.id">
                            {{ (getPubgId(member.user) || getMlbbId(member.user)) || '—' }}
                          </div>
                        </template>
                      </b-table>
                    </div>
                    <div v-else class="ui-muted text-center py-3">Информация отсутствует</div>
                  </template>
                  <template #cell(date)="data">
                    {{ formatDateTime(data.item.tournament.start_time) }}
                  </template>
                </b-table>
              </div>

              <div class="ui-foot">
                <b-pagination class="ui-pagination my-0" v-model="teamTournamentLogsCurrentPage"
                              :total-rows="totalPagesOfTeamTournamentLogs" :per-page="10" :limit="7"
                              @change="handleTournamentLogsPageChange"/>
              </div>
            </div>
          </section>

          <!-- ============ ЛОГИ ============ -->
          <section v-show="activeTab === 'logs'" class="tf-sec">
            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-history"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">Логи команды</h3>
                </div>
              </div>
              <div class="tf-card__body ui-flush">
                <b-table
                    class="ui-table-bv"
                    responsive borderless hover show-empty
                    :fields="fieldsLogos"
                    :items="teamLogs"
                    empty-text="Логов нет">
                  <template #cell(date)="data">
                    {{ formatDateTime(data.item.created_at) }}
                  </template>
                  <template #cell(type)="data">
                    <span class="ui-tag">{{ data.item.type.code }}</span>
                  </template>
                  <template #cell(reason)="data">
                    <div class="ui-log">
                      <div>
                        {{ getCodeStateMessage(data.item.type.code, data.item.data).description }}
                        <template v-if="data.item.tournament">{{ data.item.tournament.name }}</template>
                        <span v-if="data.item.type.code !== 'name-changed' && data.item.data">
                          {{ data.item.data.new_name }}
                        </span>
                        <template v-if="data.item.user">{{ data.item.user.username }}</template>
                      </div>
                      <div v-if="data.item.data && data.item.data.tournament">
                        Турнир: {{ data.item.data.tournament.name }}
                      </div>
                      <div v-if="data.item.data && data.item.data.stage">
                        Этап: {{ data.item.data.stage.name }}
                      </div>
                      <div v-if="data.item.data && data.item.data.old_stage">
                        Из этапа: {{ data.item.data.old_stage.name }}
                      </div>
                      <div v-if="data.item.data && data.item.data.new_stage">
                        В этап: {{ data.item.data.new_stage.name }}
                      </div>
                      <div v-if="data.item.data && data.item.data.group">
                        Группа: {{ data.item.data.group.name }}
                      </div>
                      <div v-if="data.item.data && data.item.data.reject_description">
                        Причина: {{ data.item.data.reject_description }}
                      </div>
                      <div v-if="data.item.type.code === 'role-assigned'">
                        {{ getRoleStateMessage(data.item.data.old_role, data.item.data.role).role }}
                      </div>
                    </div>
                  </template>
                  <template #cell(who)="data">
                    {{ (data.item.author && data.item.author.username) || '—' }}
                  </template>
                </b-table>
              </div>

              <div class="ui-foot">
                <b-pagination class="ui-pagination my-0" v-model="teamLogsCurrentPage"
                              :total-rows="teamLogsTotal" :per-page="10" :limit="7"
                              @change="logsHandlePageChange"/>
              </div>
            </div>
          </section>

          <!-- ============ РЕЙТИНГ ============ -->
          <section v-show="activeTab === 'rating'" class="tf-sec">
            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-star-o"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">Начисления рейтинга</h3>
                  <p class="tf-card__sub">Правки сохраняются сразу после изменения поля</p>
                </div>
                <span v-if="ratingRows.length" class="tf-badge tf-badge--primary">
                  {{ ratingTotal }} pts
                </span>
                <button type="button" class="tf-btn-primary" @click.prevent="show = true">
                  <i class="fa fa-plus"></i> Добавить
                </button>
              </div>
              <div class="tf-card__body ui-flush">
                <b-table class="ui-table-bv ui-table-rating" :fields="fieldRating" :items="ratingRows"
                         responsive borderless hover show-empty>
                  <template #cell(points)="{item}">
                    <div class="ui-rate">
                      <b-form-input type="number" step="0.01" v-model="item.points"
                                    class="ui-rate__input"
                                    @change="saveRatingRow(item)"/>
                      <span class="ui-rate__unit">pts</span>
                    </div>
                  </template>
                  <!-- описание читается как текст, а редактируется по клику -->
                  <template #cell(description)="{item}">
                    <b-form-input v-model="item.description" class="ui-quiet"
                                  :title="item.description"
                                  placeholder="без описания"
                                  @change="saveRatingRow(item)"/>
                  </template>
                  <template #cell(created_at)="{item}">
                    <span class="ui-dim">{{ formatDateTime(item.created_at) }}</span>
                  </template>
                  <template #cell(state)="{item}">
                    <div class="ui-rate__state">
                      <span v-if="savedRatingId === item.id" class="ui-saved">
                        <i class="fa fa-check"></i> сохранено
                      </span>
                      <b-spinner v-else-if="savingRatingId === item.id" small/>
                      <button v-else type="button" class="ui-copy"
                              @click="copyValue(item.id, 'ID начисления')"
                              v-b-tooltip.hover.left="'Скопировать ID начисления'">
                        #{{ item.id }} <i class="fa fa-copy"></i>
                      </button>
                    </div>
                  </template>
                  <template #empty>
                    <div class="ui-empty">
                      <i class="fa fa-star-o"></i>
                      <div class="ui-empty__title">Начислений пока нет</div>
                      <div class="ui-empty__text">Рейтинг за турниры появится здесь автоматически</div>
                    </div>
                  </template>
                </b-table>
              </div>
            </div>
          </section>
        </div>
    </div>
  </div>
</template>

<script>
import {TeamsAdapterMixin} from "@/mixins/Teams/TeamsAdapterMixin";
import {UsersAdapterMixin} from "@/mixins/Users/UsersAdapterMixin";
import {ROLES} from '@/constants/TeamMember/roles';
import {CODES} from '@/constants/TeamMember/logCodes';
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";
import moment from 'moment'
import TeamMembersTable from "@/components/TeamInfo/TeamMembersTable.vue";
import TeamImagePug from '@/assets/images/pugs/team.png'
import api from "@/api/api";

export default {
  name: 'TeamInfo',
  components: {TeamMembersTable},
  mixins: [TeamsAdapterMixin, UsersAdapterMixin, PaginationHelperMixin],

  created() {
    (async () => {
      try {
        // Загружаем команды и другие данные
        await Promise.all([
          this.showTeams(this.$route.params.id),
          this.showTeamRating(this.$route.params.id),
          this.shortTeamRating(this.$route.params.id),
          this.recordPageToStorage(this.teamTournamentLogsCurrentPage, this.teamTournamentLogsPageKey),
          api.teams.teamTournamentLogs({
            teamId: this.$route.params.id,
            page: this.getCurrentPageNumber(this.teamTournamentLogsPageKey),
          }),
          this.recordPageToStorage(this.teamLogsCurrentPage, this.pageKeyLogs),
          this.teamLogsShow({
            teamId: this.$route.params.id,
            pageNumber: this.getCurrentPageNumber(this.pageKeyLogs),
          })
        ]);

        // Устанавливаем турниры
        const tournamentLogs = (await api.teams.teamTournamentLogs({
          teamId: this.$route.params.id,
          page: this.teamTournamentLogsCurrentPage
        })).data;
        this.teamTournamentLogs = tournamentLogs.entry_logs || [];
        this.totalPagesOfTeamTournamentLogs = tournamentLogs.pagination.total;

        // Проверяем division.id после загрузки данных
        if (this.teamsShow && this.teamsShow.division) {
          this.isProDivision = this.teamsShow.division.id === 5;
        } else {
          this.isProDivision = false; // Убедимся, что есть fallback
        }

        // Загружаем список регионов
        this.proTeamsRegionsList = await this.fetchProTeamRegions();
        this.regionsOptions = [
          {text: 'Выберите регион', value: null, disabled: true}, // Значение по умолчанию
          ...this.proTeamsRegionsList.map((region) => ({
            text: region.name,
            value: region.id
          }))
        ];

        // Проверяем, привязан ли регион
        if (this.teamsShow.hasOwnProperty('proTeamRegionRating')) {
          this.selectedProRegionId = this.teamsShow.proTeamRegionRating.proTeamRatingRegion.id;
        }
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
      }
    })();
  },
  data() {
    return {
      TeamImagePug,
      moment,
      pageKeyLogs: 'pageNumberTeamLogs',
      teamTournamentLogsPageKey: 'teamTournamentLogsPageNumber',
      teamTournamentLogs: [],
      totalPagesOfTeamTournamentLogs: 1,
      teamTournamentLogsCurrentPage: 1,
      fields: [
        {key: 'id', label: '№',},
        {key: 'nick', label: 'Nick',},
        {key: 'pubg_nick', label: 'Pubg nick'},
        {key: 'pubg_id', label: 'Pubg id',},
        {key: 'status', label: 'Cтатус',},
        {key: 'country', label: 'Страна',},
        {key: 'socials', label: 'Социальные сети',},
        {key: 'role', label: 'Роль',},
        {key: 'inspect', label: 'Просмотр',},
      ],
      tournamentLogsFields: [
        {key: 'id', label: '№',},
        {key: 'name', label: 'Название',},
        {key: 'format', label: 'Формат'},
        {key: 'details', label: 'Детали',},
        {key: 'date', label: 'Дата турнира',},
      ],
      fieldsTournaments: [
        {key: 'id', label: 'ID', sortable: true},
        {key: 'name', label: 'Название', sortable: true},
        {key: 'date', label: 'Дата', sortable: true},
        {key: 'format', label: 'Формат', sortable: false},
      ],
      fieldsLogos: [
        {key: 'id', label: '№',},
        {key: 'date', label: 'Дата и время',},
        {key: 'type', label: 'Тип'},
        {key: 'reason', label: 'Описание',},
        {key: 'who', label: 'Кто',},
      ],
      fieldRating: [
        {key: 'points', label: 'Рейтинг'},
        {key: 'description', label: 'Описание'},
        {key: 'created_at', label: 'Добавлено'},
        {key: 'state', label: ''},
      ],
      tournamentLogFields: [
        {key: 'id', label: 'id', sortable: true},
        {key: 'stage', label: 'Стадия', sortable: true},
        {key: 'members', label: 'Состав', sortable: true},
        {key: 'game_id', label: 'Game ID', sortable: false},
      ],
      striped: true,
      bordered: true,
      totalRows: 10,
      show: false,
      isProDivision: false,
      isLoadingToggleProDivision: false,
      // Banner upload
      isDragover: false,
      bannerFile: null,
      bannerPreview: null,
      proTeamsRegionsList: [],
      regionsOptions: [],
      selectedProRegionId: null,
      activeTab: 'main',
      stickyFrame: null,
      savingRatingId: null,
      savedRatingId: null,
      savedRatingTimer: null,

      newTeamName: '',
      isShowRenameModal: false,
      isRenaming: false,
      renameError: null,

      isShowDeleteTeamModal: false,
      isDeletingTeam: false,
      deleteTeamError: null,
    }
  },
  computed: {
    /** Стор кладёт в showTeams пустой массив до загрузки — приводим к объекту. */
    team() {
      const raw = this.teamsShow;
      return raw && !Array.isArray(raw) ? raw : {};
    },

    tabs() {
      return [
        {key: 'main', label: 'Общее', icon: 'fa-shield', count: null},
        // счётчик турниров показываем только в шапке: в пагинации и в профиле команды числа разные
        {key: 'tournaments', label: 'Турниры', icon: 'fa-trophy', count: null},
        {key: 'logs', label: 'Логи', icon: 'fa-history', count: null},
        {key: 'rating', label: 'Рейтинг', icon: 'fa-star-o', count: (this.getTeamCurrentRating || []).length || null},
      ];
    },

    /** Всё, что раньше дублировалось в шапке, сводке и карточке «О команде». */
    gameName() {
      return (this.team.game && this.team.game.name) || 'дисциплина не указана';
    },

    countryName() {
      return (this.team.country && this.team.country.name) || 'страна не указана';
    },

    divisionName() {
      return (this.team.division && this.team.division.name) || '—';
    },

    ratingValue() {
      return this.team.rating !== null && this.team.rating !== undefined ? this.team.rating : '—';
    },

    membersCount() {
      return (this.members || []).length;
    },

    ratingRows() {
      return this.getTeamCurrentRating || [];
    },

    /** Сумма начислений — в шапке карточки, чтобы не считать глазами по строкам. */
    ratingTotal() {
      const total = this.ratingRows.reduce((sum, row) => sum + (Number(row.points) || 0), 0);
      return Number.isInteger(total) ? String(total) : total.toFixed(2);
    },

    hasMembersTable() {
      return ['pubg-mobile', 'mlbb'].includes(this.team.game && this.team.game.slug);
    },

    logoUrl() {
      return (this.team.logo && this.team.logo.original_url) || TeamImagePug;
    },

    hasLogo() {
      return Boolean(this.team.logo && this.team.logo.original_url);
    },

    bans() {
      return this.team.currentBans || [];
    },

    owner() {
      const list = this.members || [];
      const found = list.find(member => member.role_in_team && member.role_in_team.code === 'owner');
      return found ? (found.username || found.email) : '';
    },

    isRenameValid() {
      return typeof this.newTeamName === 'string' && this.newTeamName.trim().length >= 2;
    },
  },
  mounted() {
    this.updateStickyOffsets();
    window.addEventListener('resize', this.updateStickyOffsets);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateStickyOffsets);
    if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
    if (this.savedRatingTimer) clearTimeout(this.savedRatingTimer);
  },
  watch: {
    selectedProRegionId(newRegionId, oldRegionId) {

      if (newRegionId && !this.teamsShow.hasOwnProperty('proTeamRegionRating')) {
        this.attachProTeamRegion(this.teamsShow.id, newRegionId);
      }
    }
  },
  methods: {
    /** Шапка приложения фиксированная и её высота зависит от темы — считаем оффсет по факту. */
    updateStickyOffsets() {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = null;
        if (!this.$el || !this.$el.style) return;
        const appHeader = document.querySelector('.page-header');
        const headerHeight = appHeader ? Math.round(appHeader.getBoundingClientRect().height) : 0;
        this.$el.style.setProperty('--tf-sticky-top', headerHeight + 'px');
      });
    },

    goTab(key) {
      this.activeTab = key;
      this.$nextTick(() => {
        const anchor = this.$refs.tabsTop;
        if (!anchor || !anchor.getBoundingClientRect) return;
        const appHeader = document.querySelector('.page-header');
        const headerHeight = appHeader ? Math.round(appHeader.getBoundingClientRect().height) : 0;
        const top = anchor.getBoundingClientRect().top + window.pageYOffset - headerHeight - 8;
        window.scrollTo({top: Math.max(top, 0), behavior: 'smooth'});
      });
    },

    goBack() {
      this.$router.push({name: 'teams'});
    },

    copyValue(value, label) {
      if (value === null || value === undefined || value === '') return;
      const text = String(value);
      const done = () => this.$bvToast.toast(`${label} скопирован`, {title: 'Готово', variant: 'success', solid: true});
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
        });
        return;
      }
      const area = document.createElement('textarea');
      area.value = text;
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand('copy');
        done();
      } finally {
        document.body.removeChild(area);
      }
    },

    openRenameModal() {
      this.newTeamName = this.team.name || '';
      this.renameError = null;
      this.isShowRenameModal = true;
    },

    /** Название правим в модалке: раньше поле писало прямо в стор и шапка менялась до сохранения. */
    async confirmRename() {
      if (!this.isRenameValid || this.isRenaming) return;
      this.isRenaming = true;
      this.renameError = null;
      try {
        await this.updateTeamName({teamId: this.$route.params.id, name: this.newTeamName.trim()});
        await this.showTeams(this.$route.params.id);
        this.isShowRenameModal = false;
      } catch (error) {
        this.renameError = (error && error.response && error.response.data && error.response.data.message)
            || 'Не удалось переименовать команду';
      } finally {
        this.isRenaming = false;
      }
    },

    formatDateTime(value) {
      if (!value) return '—';
      const date = new Date(String(value).replace(' ', 'T'));
      if (isNaN(date.getTime())) return '—';
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    handleOpenEditAvatar() {
      this.$bvModal.show('modal-edit-avatar')
    },
    getPubgId(member) {
      return member.game_profiles
          ?.find(p => p.fields?.pubg_id)
          ?.fields?.pubg_id;
    },
    async handleDownloadTeamAvatar(imgUrl, name) {
      try {

        const link = document.createElement('a')
        link.href = imgUrl
        link.download = name
        link.target = '_blank'
        document.body.appendChild(link);
        link.click()
        document.body.removeChild(link);

        this.$bvToast.toast('Логотип успешно скачен!', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Ошибка при скачивании логотипа:', error);
        this.$bvToast.toast('Не удалось скачать логотип', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });

      }
    },
    getMlbbId(member) {
      return member.game_profiles
          ?.find(p => p.fields?.mlbb_id)
          ?.fields?.mlbb_id;
    },
    handleLogoClick() {
      // Открываем окно выбора файла
      this.$refs.logoInput.click();
    },
    handleLogoSelect(event) {
      const file = event.target.files[0];
      // Проверяем, что файл есть и что это изображение
      if (file && file.type.startsWith('image/')) {
        this.uploadLogo(file);
      }
      this.$bvModal.hide('modal-edit-avatar');
    },
    async deleteLogo() {
      try {
        await api.teams.removeTeamLogo(this.$route.params.id);
        await this.showTeams(this.$route.params.id);

        this.$bvToast.toast('Логотип успешно удалён!', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Ошибка при удалении логотипа:', error);
        this.$bvToast.toast('Не удалось удалить логотип', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },
    async uploadLogo(file) {
      const formData = new FormData();
      formData.append('logo', file);
      try {
        await api.teams.uploadTeamLogo(this.$route.params.id, formData);
        // Обновляем данные команды, чтобы сразу увидеть новое лого
        await this.showTeams(this.$route.params.id);
        this.$bvToast.toast('Логотип успешно загружен!', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        if (error.response.status === 413) {
          this.$bvToast.toast('Слишком большой размер файла', {
            title: 'Ошибка',
            variant: 'danger',
            solid: true
          });
          return;
        }
        this.$bvToast.toast('Не удалось загрузить логотип', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.$refs.logoInput.value = '';
      }
    },
    acceptTeamRating() {
      this.addTeamRating({
        team_id: this.$route.params.id,
        points: this.newRating,
        description: this.newDescription
      }).then(() => {
        this.show = false
        this.shortTeamRating(this.$route.params.id)
      })
    },
    updateCurrentTeamRating(points, id, description) {
      return this.updateTeamRating({points: points, id: id, team_id: this.$route.params.id, description: description})
    },

    /** Автосохранение строки с видимым подтверждением: правки уходят без кнопки «Сохранить». */
    async saveRatingRow(item) {
      this.savingRatingId = item.id;
      this.savedRatingId = null;
      if (this.savedRatingTimer) clearTimeout(this.savedRatingTimer);

      try {
        await this.updateCurrentTeamRating(item.points, item.id, item.description);
        this.savedRatingId = item.id;
        this.savedRatingTimer = setTimeout(() => {
          this.savedRatingId = null;
        }, 2000);
      } finally {
        this.savingRatingId = null;
      }
    },
    getRoleStateMessage(old_role, role) {
      return {role: `Был ${ROLES[old_role]} - Стал ${ROLES[role]}`}
    },
    getCodeStateMessage(code, data = null) {
      if (code === "name-changed" && data?.old_name && data?.new_name) {
        return {
          description: `Изменено название команды ${data.old_name ?? ""} на ${data.new_name ?? ""}`
        }
      }
      return {description: `${CODES[code]}`};
    },
    logsHandlePageChange(pageNumber) {
      this.recordPageToStorage(pageNumber, this.pageKeyLogs)
      this.teamLogsShow({
        teamId: this.$route.params.id,
        pageNumber: pageNumber,
      })
    },
    async handleTournamentLogsPageChange(pageNumber) {
      const params = {page: pageNumber, teamId: this.$route.params.id}
      this.recordPageToStorage(pageNumber, this.teamTournamentLogsPageKey)
      const response = await api.teams.teamTournamentLogs(params)
      this.teamTournamentLogs = response.data.entry_logs || []
    },
    showTournDetails(data) {
      data.toggleDetails();
    },
    async toggleProDivision() {
      this.isLoadingToggleProDivision = true; // Устанавливаем состояние загрузки
      try {
        await (this.isProDivision
            ? api.teams.bindProDivision
            : api.teams.unbindProDivision)(this.$route.params.id);

        if (!this.isProDivision && this.selectedProRegionId) {
          await this.detachProTeamRegion(this.teamsShow.id);
        }
      } catch (error) {
        console.error('Error toggling pro division:', error);
        this.isProDivision = !this.isProDivision; // Возвращаем предыдущее состояние при ошибке
        this.$bvToast.toast('Не удалось изменить статус Pro Division', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.isLoadingToggleProDivision = false; // Сбрасываем состояние загрузки
      }
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      this.processFile(file);
    },

    handleFileDrop(event) {
      this.isDragover = false;
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    },
    processFile(file) {
      if (file && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.bannerPreview = e.target.result;
          this.bannerFile = file;
        };
        reader.readAsDataURL(file);
      }
    },
    async uploadBanner() {
      if (!this.bannerFile) return;

      const formData = new FormData();
      formData.append('file', this.bannerFile);
      formData.append('_method', 'patch');

      try {
        await api.teams.uploadBanner(this.$route.params.id, formData);
        // Refresh the team data to get the new banner
        await this.showTeams(this.$route.params.id);
        this.bannerPreview = null;
        this.bannerFile = null;
        this.$bvToast.toast('Баннер успешно загружен', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Error uploading banner:', error);
        this.$bvToast.toast('Не удалось загрузить баннер', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },
    cancelUpload() {
      this.bannerPreview = null;
      this.bannerFile = null;
    },
    async deleteBanner() {
      try {
        await api.teams.deleteBanner(this.$route.params.id);
        // Refresh the team data to remove the banner
        await this.showTeams(this.$route.params.id);
        this.$bvToast.toast('Баннер успешно удален', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Error deleting banner:', error);
        this.$bvToast.toast('Не удалось удалить баннер', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },
    async fetchProTeamRegions() {
      try {
        const response = await api.teams.proTeamRegions();
        if (response.status === 200) {
          return response.data.data;
        } else {
          console.error(`Error: Received status ${response.status}`);
          return [];
        }
      } catch (error) {
        console.error('Error fetching pro team regions:', error);
        return [];
      }
    },
    async detachProTeamRegion(teamId) {
      try {
        const response = await api.teams.detachProTeamRegion(teamId);
        if (response.status === 204) {
          this.clearRegion()
          this.$bvToast.toast('Регион успешно отвязан', {
            title: 'Успех',
            variant: 'success',
            solid: true
          });
        } else {
          console.error(`Ошибка: получен статус ${response.status}`);
          this.$bvToast.toast('Не удалось отвязать регион', {
            title: 'Ошибка',
            variant: 'danger',
            solid: true
          });
        }
      } catch (error) {
        console.error('Ошибка отвязки региона:', error);
        this.$bvToast.toast('Не удалось отвязать регион', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },
    async attachProTeamRegion(teamId, regionId) {
      try {
        const response = await api.teams.attachProTeamRegion(teamId, regionId);
        if (response.status === 204) {
          this.$bvToast.toast('Регион успешно привязан', {
            title: 'Успех',
            variant: 'success',
            solid: true
          });
        } else {
          this.$bvToast.toast('Не удалось привязать регион', {
            title: 'Ошибка',
            variant: 'danger',
            solid: true
          });
        }
      } catch (error) {
        console.error('Ошибка привязки региона:', error);
        this.$bvToast.toast('Не удалось привязать регион', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },
    clearRegion() {
      this.selectedProRegionId = null;
    },
    openDeleteTeamModal() {
      this.deleteTeamError = null;
      this.isShowDeleteTeamModal = true;
    },
    async confirmDeleteTeam() {
      this.isDeletingTeam = true;
      this.deleteTeamError = null;
      try {
        await api.teams.deleteTeam(this.$route.params.id);
        this.isShowDeleteTeamModal = false;
        this.$bvToast.toast('Команда успешно удалена', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
        this.$router.push({name: 'teams'});
      } catch (error) {
        this.deleteTeamError = error?.response?.data?.message || 'Не удалось удалить команду';
      } finally {
        this.isDeletingTeam = false;
      }
    },
  },
}
</script>
<style scoped lang="scss">
@import "~@/assets/scss/tournament-form.scss";

/* ---------- шапка команды ---------- */
.ui-hero {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 14px 16px;
  margin-bottom: 12px;
  background: var(--tf-bg);
  border: 1px solid var(--tf-border);
  border-radius: var(--tf-radius);
  box-shadow: var(--tf-shadow);

  &__logo {
    position: relative;
    flex: 0 0 auto;
    width: 56px;
    height: 56px;
    padding: 0;
    border: 0;
    border-radius: 18px;
    background: var(--tf-soft);
    overflow: hidden;
    cursor: pointer;

    img { width: 100%; height: 100%; object-fit: cover; }

    &-edit {
      position: absolute;
      inset: auto 0 0 0;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, .55);
      color: #fff;
      font-size: 10px;
      opacity: 0;
      transition: opacity .16s ease;
    }

    &:hover &-edit { opacity: 1; }
  }

  &__title { min-width: 0; flex: 1 1 260px; }

  &__name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__name {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--tf-text);
    word-break: break-word;

    &.is-placeholder { color: var(--tf-muted); font-weight: 500; }
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 4px;
    font-size: 11.5px;
    color: var(--tf-muted);
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }
}

/* ---------- цифры команды ---------- */
.ui-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;

  > div {
    min-width: 96px;
    padding: 7px 12px;
    border-radius: 12px;
    background: var(--tf-soft);
  }

  dt {
    margin: 0;
    font-size: 11px;
    font-weight: 500;
    color: var(--tf-muted);
    white-space: nowrap;
  }

  dd {
    margin: 2px 0 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--tf-text);
  }
}

@media (max-width: 767px) {
  .ui-hero__actions { margin-left: 0; width: 100%; }

  .ui-stats > div { flex: 1 1 calc(50% - 8px); }
}

/* ---------- меню «ещё действия» ---------- */
.ui-hero ::v-deep .ui-kebab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid var(--tf-border);
  border-radius: 10px;
  background: var(--tf-bg);
  color: var(--tf-muted);

  &:hover, &:focus { border-color: var(--tf-primary); color: var(--tf-primary); box-shadow: none; }
}

.ui-hero ::v-deep .ui-menu {
  min-width: 210px;
  padding: 6px;
  border: 1px solid var(--tf-border);
  border-radius: 12px;
  box-shadow: var(--tf-shadow);

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    border-radius: 9px;
    padding: 8px 10px;
    font-size: 13px;
    color: var(--tf-text);

    &:hover { background: var(--tf-soft); }

    i { width: 14px; color: var(--tf-muted); text-align: center; }
  }

  .ui-menu__danger {
    color: var(--tf-danger);

    i { color: var(--tf-danger); }

    &:hover { background: var(--tf-danger-soft); color: var(--tf-danger); }
  }
}

/* ---------- горизонтальные табы ---------- */
.ui-tabs {
  position: sticky;
  top: var(--tf-sticky-top, 0px);
  z-index: 8;
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding: 6px;
  margin-bottom: 16px;
  background: var(--tf-bg);
  border: 1px solid var(--tf-border);
  border-radius: var(--tf-radius);
  box-shadow: var(--tf-shadow);
}

.ui-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--tf-text);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;

  > i:first-child { color: var(--tf-muted); font-size: 13px; }

  &:hover { background: var(--tf-soft); }

  &.is-on {
    background: var(--tf-primary-soft);
    color: var(--tf-primary);

    > i:first-child { color: var(--tf-primary); }
  }
}

.ui-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 19px;
  height: 19px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--tf-soft-2);
  color: var(--tf-muted);
  font-size: 10.5px;
  font-weight: 700;
}

.ui-tab.is-on .ui-count { background: var(--tf-primary); color: #fff; }

/* ---------- одна колонка контента ---------- */
.ui-main { min-width: 0; }

.ui-logo-modal {
  display: flex;
  justify-content: center;

  &__img {
    width: 160px;
    height: 160px;
    border-radius: 32px;
    object-fit: cover;
    background: var(--tf-soft);
  }
}

.ui-copy {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 0;
  color: inherit;
  font-size: inherit;
  cursor: pointer;

  i { opacity: 0; font-size: 10px; transition: opacity .14s ease; }

  &:hover {
    color: var(--tf-primary);

    i { opacity: 1; }
  }
}

.ui-muted { color: var(--tf-muted); font-size: 13px; }

/* ---------- бан ---------- */
.ui-ban {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin-top: 6px;
  font-size: 12.5px;
  color: var(--tf-text);

  b { color: var(--tf-danger); }

  em { font-style: normal; color: var(--tf-muted); }
}

/* ---------- регион pro-дивизиона ---------- */
.ui-region {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;

  ::v-deep .custom-select { max-width: 280px; }
}

.ui-switch {
  flex: 0 0 auto;
  margin: 0;

  ::v-deep .custom-control-label { cursor: pointer; }
}

/* ---------- кнопки-опасность ---------- */
.ui-btn-danger {
  border-color: var(--tf-danger);
  color: var(--tf-danger) !important;

  &:hover { background: var(--tf-danger-soft); border-color: var(--tf-danger); color: var(--tf-danger) !important; }
}

.ui-btn-danger-solid {
  background: var(--tf-danger);

  &:hover { filter: brightness(1.06); }
}

.ui-icon-danger:hover {
  border-color: var(--tf-danger);
  color: var(--tf-danger);
}

/* ---------- баннер ---------- */
.ui-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 160px;
  padding: 20px;
  border: 1px dashed var(--tf-border);
  border-radius: 12px;
  background: var(--tf-soft);
  color: var(--tf-muted);
  font-size: 13px;
  text-align: center;
  cursor: pointer;
  transition: border-color .16s ease, background .16s ease;

  &:hover { border-color: var(--tf-primary); }

  &.is-over {
    border-color: var(--tf-primary);
    background: var(--tf-primary-soft);
    color: var(--tf-primary);
  }
}

.ui-banner {
  &__img {
    display: block;
    width: 100%;
    max-height: 260px;
    object-fit: cover;
    border-radius: 12px;
    background: var(--tf-soft-2);
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 12px;
  }
}

/* ---------- таблицы bootstrap-vue внутри карточек ---------- */
.ui-flush { padding: 0; }

.ui-table-bv ::v-deep {
  margin: 0;
  color: var(--tf-text);

  thead th {
    background: var(--tf-bg);
    border: 0 !important;
    border-bottom: 1px solid var(--tf-border) !important;
    padding: 11px 16px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .05em;
    color: var(--tf-muted);
    white-space: nowrap;
  }

  tbody td {
    border: 0 !important;
    border-bottom: 1px solid var(--tf-border) !important;
    padding: 12px 16px;
    font-size: 13px;
    vertical-align: middle;
  }

  tbody tr:last-child td { border-bottom: 0 !important; }

  tbody tr:hover td { background: var(--tf-soft); }
}

.ui-subtable {
  padding: 4px 16px 16px;
  background: var(--tf-soft);
}

.ui-act {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid var(--tf-border);
  border-radius: 9px;
  background: var(--tf-bg);
  color: var(--tf-muted);
  font-size: 12px;
  cursor: pointer;

  &:hover { border-color: var(--tf-primary); background: var(--tf-primary-soft); color: var(--tf-primary); }
}

.ui-tag {
  display: inline-flex;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--tf-primary-soft);
  color: var(--tf-primary);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.ui-log {
  font-size: 12.5px;
  line-height: 1.5;
  max-width: 560px;

  > div + div { color: var(--tf-muted); font-size: 11.5px; }
}

/* ---------- начисления рейтинга ---------- */
.ui-table-rating ::v-deep {
  tbody td { padding-top: 6px; padding-bottom: 6px; }

  thead th:first-child, tbody td:first-child { width: 150px; }

  thead th:nth-child(3), tbody td:nth-child(3) { width: 170px; white-space: nowrap; }

  thead th:last-child, tbody td:last-child { width: 130px; text-align: right; }
}

.ui-rate {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &__unit { font-size: 11px; color: var(--tf-muted); }

  &__state {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-height: 32px;
  }
}

.ui-table-rating ::v-deep {
  .ui-rate__input {
    width: 84px;
    height: 32px;
    padding: 0 8px;
    border: 1px solid var(--tf-border);
    border-radius: 9px;
    background: var(--tf-bg);
    font-size: 13px;
    font-weight: 600;
    text-align: right;

    /* стрелки спиннера сжимают поле и в админке не нужны */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  }

  /* поле-невидимка: строка читается как текст, правится по клику */
  .ui-quiet {
    width: 100%;
    height: 32px;
    padding: 0 10px;
    border: 1px solid transparent;
    border-radius: 9px;
    background: transparent;
    font-size: 13px;

    &:hover { background: var(--tf-soft); }

    &:focus {
      background: var(--tf-bg);
      border-color: var(--tf-primary);
      box-shadow: 0 0 0 3px var(--tf-primary-soft);
    }
  }
}

.ui-dim { font-size: 12.5px; color: var(--tf-muted); }

.ui-saved {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--tf-ok);
}

.ui-empty {
  padding: 34px 16px;
  text-align: center;
  color: var(--tf-muted);

  > i { font-size: 22px; opacity: .6; }

  &__title { margin-top: 10px; font-size: 14px; font-weight: 600; color: var(--tf-text); }

  &__text { margin-top: 2px; font-size: 12.5px; }
}

/* ---------- подвал с пагинацией ---------- */
.ui-foot {
  display: flex;
  justify-content: flex-end;
  padding: 13px 16px;
  border-top: 1px solid var(--tf-border);
  background: var(--tf-soft);
}

.ui-pagination ::v-deep {
  .page-item .page-link {
    border: 0;
    margin: 0 2px;
    min-width: 32px;
    height: 32px;
    line-height: 32px;
    padding: 0;
    text-align: center;
    border-radius: 9px;
    color: var(--tf-muted);
    background: transparent;
    font-size: 13px;
  }

  .page-item .page-link:hover { background: var(--tf-soft-2); color: var(--tf-primary); }

  .page-item.active .page-link { background: var(--tf-primary); color: #fff !important; }

  .page-item.disabled .page-link { opacity: .4; }
}
</style>
